import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Grid,
  Box,
  Typography,
  MenuItem,
} from '@mui/material';

/**
 * Document Type Form Dialog
 * Add/Edit dialog for document types
 * Uses mock data during UI-First phase
 */
const DocumentTypeFormDialog = ({ open, onClose, onSave, documentType, categories, verticalId }) => {
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    nameAr: '',
    categoryId: '',
    hasExpiry: true,
    isRenewable: false,
    documentNumberLabel: '',
    documentNumberPattern: '',
    issuingAuthority: '',
    icon: 'description',
    isActive: true
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const isEdit = !!documentType;

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      if (documentType) {
        setFormData({
          code: documentType.code || '',
          name: documentType.name || '',
          nameAr: documentType.nameAr || '',
          categoryId: documentType.categoryId || '',
          hasExpiry: documentType.hasExpiry !== undefined ? documentType.hasExpiry : true,
          isRenewable: documentType.isRenewable || false,
          documentNumberLabel: documentType.documentNumberLabel || '',
          documentNumberPattern: documentType.documentNumberPattern || '',
          issuingAuthority: documentType.issuingAuthority || '',
          icon: documentType.icon || 'description',
          isActive: documentType.isActive !== undefined ? documentType.isActive : true
        });
      } else {
        setFormData({
          code: '',
          name: '',
          nameAr: '',
          categoryId: categories[0]?.id || '',
          hasExpiry: true,
          isRenewable: false,
          documentNumberLabel: '',
          documentNumberPattern: '',
          issuingAuthority: '',
          icon: 'description',
          isActive: true
        });
      }
      setErrors({});
    }
  }, [open, documentType, categories]);

  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.code.trim()) {
      newErrors.code = 'Code is required';
    } else if (!/^[A-Z_]+$/.test(formData.code)) {
      newErrors.code = 'Code must be uppercase letters and underscores only';
    }

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.categoryId) {
      newErrors.categoryId = 'Category is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setSaving(true);
    try {
      await onSave(formData);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isEdit ? 'Edit Document Type' : 'Add New Document Type'}
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {/* Basic Info */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Basic Information
            </Typography>
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              label="Code"
              value={formData.code}
              onChange={handleChange('code')}
              error={!!errors.code}
              helperText={errors.code || 'e.g., TRADE_LICENSE'}
              fullWidth
              required
              disabled={isEdit}
              inputProps={{ style: { textTransform: 'uppercase' } }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              label="Name"
              value={formData.name}
              onChange={handleChange('name')}
              error={!!errors.name}
              helperText={errors.name}
              fullWidth
              required
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              select
              label="Category"
              value={formData.categoryId}
              onChange={handleChange('categoryId')}
              error={!!errors.categoryId}
              helperText={errors.categoryId}
              fullWidth
              required
            >
              {categories.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: 0.5, backgroundColor: category.colour || category.color }} />
                    {category.name}
                  </Box>
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Name (Arabic)"
              value={formData.nameAr}
              onChange={handleChange('nameAr')}
              fullWidth
              dir="rtl"
              placeholder="الاسم بالعربية"
            />
          </Grid>

          {/* Document Number */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
              Document Number
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Document Number Label"
              value={formData.documentNumberLabel}
              onChange={handleChange('documentNumberLabel')}
              fullWidth
              placeholder="e.g., License Number, TRN"
              helperText="Label shown in upload form"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Validation Pattern (Regex)"
              value={formData.documentNumberPattern}
              onChange={handleChange('documentNumberPattern')}
              fullWidth
              placeholder="e.g., ^100\d{12}$"
              helperText="Leave empty for no validation"
            />
          </Grid>

          {/* Issuing Authority */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
              Issuing Authority
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Issuing Authority"
              value={formData.issuingAuthority}
              onChange={handleChange('issuingAuthority')}
              fullWidth
              placeholder="e.g., Department of Economic Development"
            />
          </Grid>

          {/* Expiry Settings */}
          <Grid item xs={12} sm={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.hasExpiry}
                  onChange={handleChange('hasExpiry')}
                  color="primary"
                />
              }
              label="Has Expiry Date"
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isRenewable}
                  onChange={handleChange('isRenewable')}
                  color="primary"
                  disabled={!formData.hasExpiry}
                />
              }
              label="Is Renewable"
            />
          </Grid>

          {/* Status */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={handleChange('isActive')}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2">Active</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Inactive document types won't be available for upload
                  </Typography>
                </Box>
              }
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving}
        >
          {saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentTypeFormDialog;
